/*
 *  ************************************************************************
 *  *  © [2015 - 2024] Quintype Technologies India Private Limited
 *  *  All Rights Reserved.
 *  *************************************************************************
 */
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { parseUrl } from "query-string";
import { checkIfSingleSignOn } from "../../sso-utils";
import { SvgHandler } from "../../../../svg-handler/svg-handler";
import googleIcon from "../../../../../../assets/icons/google.svg";
import facebookIcon from "../../../../../components/basic/social-follow/social-components/circular-color/svg/facebook-circle-color.svg";
import linkedinIcon from "../../../../../components/basic/social-follow/social-components/square/svg/linkedin-square.svg";
import appleIcon from "../../../../../../assets/icons/apple.svg";
import get from "lodash/get";
import { withGoogleLogin, withFacebookLogin, withLinkedinLogin, withAppleLogin } from "@quintype/bridgekeeper-js";
import "./social-login.module.css";
import {
  CONTINUE_WITH_FACEBOOK,
  CONTINUE_WITH_GOOGLE,
  CONTINUE_WITH_LINKEDIN,
  CONTINUE_WITH_APPLE,
} from "../../../../../constants";

const getOauthAuthorizeUrl = (bridgeKeeperClientId, currentPath) => {
  const params = parseUrl(currentPath);
  const callbackUrl =
    get(params, ["query", "callback_uri"], global?.localStorage?.getItem("loginRedirectUrl")) || window?.location.href;
  const redirectUrl = get(params, ["query", "redirect_uri"], `${window?.location.origin}/api/auth/v1/oauth/token`);
  return `${
    window.location.origin
  }/api/auth/v1/oauth/authorize?redirect_uri=${redirectUrl}&client_id=${bridgeKeeperClientId}&callback_uri=${encodeURIComponent(
    callbackUrl
  )}&response_type=code`;
};

const SocialLoginButton = ({ data, logo, clazzName, providerLogin, label }) => {
  const [loginPath, setLoginPath] = useState("");

  useEffect(() => {
    const { serverSideLoginPath } = providerLogin(data);
    setLoginPath(serverSideLoginPath);
    return;
  }, [providerLogin, data]);

  return (
    <a href={loginPath} target="_self" styleName={`icon-wrapper ${clazzName}`}>
      <SvgHandler xlinkHref={logo} clazzName={clazzName} width="20" height="20" />
      <span styleName="text-wrapper">{label}</span>
    </a>
  );
};

export const getSocialProvidersData = (loginData) => {
  const { googleAuth = true, facebookAuth = true, linkedInAuth = true, appleAuth = false } = get(loginData, ["pageSetting"], {});
  const {
    googleLoginLabel = "",
    facebookLoginLabel = "",
    linkedInLoginLabel = "",
    appleLoginLabel = "",
  } = get(loginData, ["sign-in", "rows", 0, "config"], {});

  const data = [
    {
      provider: "google",
      logo: googleIcon,
      clazzName: "google",
      label: googleLoginLabel || CONTINUE_WITH_GOOGLE,
      providerLogin: withGoogleLogin,
    },
    {
      provider: "facebook",
      logo: facebookIcon,
      clazzName: "facebook",
      label: facebookLoginLabel || CONTINUE_WITH_FACEBOOK,
      providerLogin: withFacebookLogin,
    },
    {
      provider: "linkedin",
      logo: linkedinIcon,
      clazzName: "linkedin",
      label: linkedInLoginLabel || CONTINUE_WITH_LINKEDIN,
      providerLogin: withLinkedinLogin,
    },
    {
      provider: "apple",
      logo: appleIcon,
      clazzName: "apple",
      label: appleLoginLabel || CONTINUE_WITH_APPLE,
      providerLogin: ({ redirectUrl } = {}) => withAppleLogin(redirectUrl),
    },
  ];
  const conditions = [
    { condition: !googleAuth, provider: "google" },
    { condition: !facebookAuth, provider: "facebook" },
    { condition: !linkedInAuth, provider: "linkedin" },
    { condition: !appleAuth, provider: "apple" },
  ];

  const filteredData = data.filter(
    (item) => !conditions.some((cond) => cond.condition && item.provider === cond.provider)
  );
  return filteredData;
};

export const SocialLogin = ({ mode }) => {
  const [urlToBeRedirected, setRedirectUrl] = useState("/");
  const { general = {}, login: loginData = {} } = useSelector((state) =>
    get(state, ["qt", "config", "pagebuilder-config"], {})
  );
  const { sso = {} } = general;
  const currentPath = useSelector((state) => get(state, ["qt", "currentPath"], ""));
  const sketchesHost = useSelector((state) => get(state, ["qt", "config", "sketches-host"], ""));
  const { bridgeKeeperClientId = "" } = sso;
  useEffect(() => {
    const oauthAuthorizeUrl = getOauthAuthorizeUrl(bridgeKeeperClientId, currentPath);
    setRedirectUrl(
      checkIfSingleSignOn(sso)
        ? oauthAuthorizeUrl
        : global?.localStorage?.getItem('loginRedirectUrl') || `${sketchesHost}`
    );
  }, [sso]);

  const data = {
    scope: "email",
    emailMandatory: true,
    redirectUrl: encodeURIComponent(urlToBeRedirected),
  };

  const socialProviders = getSocialProvidersData(loginData);

  return (
    <div styleName={mode} className="socialIconWrapper">
      {socialProviders.map((config) => (
        <SocialLoginButton key={config?.provider} data={data} {...config} />
      ))}
    </div>
  );
};
